/**
 * Domain Provisioning Card Component
 * 
 * Displays domain provisioning status during club creation.
 * Shows:
 * - Domain name
 * - Current status (pending/ready/failed)
 * - Setup instructions (copy-able)
 * - Progress indicator
 * - DNS records (if applicable)
 */

import React, { useState } from 'react';
import Card from './Card';
import Alert from './Alert';
import Badge from './Badge';
import Button from './Button';
import { Copy, Check, Clock, AlertCircle, HelpCircle, ChevronRight, ChevronDown } from 'lucide-react';
import styles from './DomainProvisioningCard.module.css';

/**
 * DomainProvisioningCard Component
 * 
 * @param {object} props
 * @param {string} props.domain - Domain name
 * @param {string} props.status - Domain status (pending|ready|failed)
 * @param {string} props.sslStatus - SSL status (provisioning|ready|failed)
 * @param {array} props.instructions - Setup instructions array
 * @param {array} props.dnsRecords - DNS records to configure
 * @param {boolean} props.isLoading - Is polling in progress
 * @param {string} props.error - Error message if any
 * @param {function} props.onRetry - Callback to retry
 * @param {boolean} props.showDetails - Show advanced details (default false)
 */
export function DomainProvisioningCard({
  domain,
  status = 'pending',
  sslStatus = 'provisioning',
  instructions = [],
  dnsRecords = [],
  isLoading = false,
  error = null,
  onRetry = null,
  showDetails = false,
}) {
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [expandedSection, setExpandedSection] = useState(null);
  const [isInstructionsExpanded, setIsInstructionsExpanded] = useState(true);

  const toggleInstructions = () => {
    setIsInstructionsExpanded(!isInstructionsExpanded);
  };

  const isReady = status === 'ready' && sslStatus === 'ready';
  const isFailed = status === 'failed' || sslStatus === 'failed';
  const isPending = status === 'pending' || sslStatus === 'provisioning';

  /**
   * Parse domain into subdomain and platform domain parts
   */
  const parseDomain = (domainStr) => {
    if (!domainStr) return { subdomain: '', platformDomain: '' };

    const parts = domainStr.split('.');
    if (parts.length < 2) return { subdomain: '', platformDomain: domainStr };

    // For domains like "subdomain.platform.com", platform is "platform.com"
    // For domains like "subdomain.localhost", platform is "localhost"
    const subdomain = parts[0];
    const platformDomain = parts.slice(1).join('.');

    return { subdomain, platformDomain };
  };

  /**
   * Get the appropriate platform domain for the current environment
   */
  const getEnvironmentPlatformDomain = () => {
    const currentHost = window.location.host;

    // Check if we're running on localhost (development)
    if (currentHost.includes('localhost')) {
      return 'localhost';
    }

    // Check if we're on theworldspitch.org (production)
    if (currentHost.includes('theworldspitch.org')) {
      return 'theworldspitch.org';
    }

    // For other environments, try to extract platform domain from current host
    // e.g., if current host is "app.customdomain.com", platform domain might be "customdomain.com"
    const hostParts = currentHost.split('.');
    if (hostParts.length >= 2) {
      return hostParts.slice(1).join('.');
    }

    // Fallback
    return 'theworldspitch.org';
  };

  /**
   * Resolve domain for current environment
   */
  const resolveDomainForEnvironment = (domainStr) => {
    if (!domainStr) return domainStr;

    const { subdomain, platformDomain: originalPlatform } = parseDomain(domainStr);
    const environmentPlatform = getEnvironmentPlatformDomain();

    // If the domain already matches the environment, return as-is
    if (originalPlatform === environmentPlatform) {
      return domainStr;
    }

    // Reconstruct with environment-appropriate platform domain
    return `${subdomain}.${environmentPlatform}`;
  };

  /**
   * Check if domain is localhost-based in current environment
   */
  const isLocalhostDomain = (domainStr) => {
    const resolved = resolveDomainForEnvironment(domainStr);
    return resolved && resolved.includes('localhost');
  };

  /**
   * Get user-facing URL for the domain
   */
  const getUserFacingUrl = (domainStr) => {
    if (!domainStr) return '';

    const resolvedDomain = resolveDomainForEnvironment(domainStr);

    // For localhost domains, add port
    if (resolvedDomain.includes('localhost')) {
      return `http://${resolvedDomain}:3000`;
    }

    // For production domains, assume HTTPS
    return `https://${resolvedDomain}`;
  };

  /**
   * Get status badge variant
   */
  const getStatusBadgeVariant = (currentStatus) => {
    switch (currentStatus) {
      case 'ready':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'info';
    }
  };

  /**
   * Get status display text
   */
  const getStatusText = (currentStatus) => {
    switch (currentStatus) {
      case 'ready':
        return <span className="flex items-center gap-1"><Check size={14} /> Ready</span>;
      case 'pending':
        return <span className="flex items-center gap-1"><Clock size={14} /> Pending</span>;
      case 'failed':
        return <span className="flex items-center gap-1"><AlertCircle size={14} /> Failed</span>;
      default:
        return <span className="flex items-center gap-1"><HelpCircle size={14} /> Unknown</span>;
    }
  };

  /**
   * Get SSL status display text
   */
  const getSSLStatusText = (sslStatus) => {
    // For local development, SSL is N/A since we use HTTP
    if (isLocalhostDomain(domain)) {
      return 'N/A';
    }
    switch (sslStatus) {
      case 'ready':
        return 'Success';
      case 'provisioning':
        return 'Provisioning';
      case 'failed':
        return 'Failed';
      default:
        return 'Unknown';
    }
  };

  /**
   * Get subdomain status display text
   */
  const getSubdomainStatusText = (status) => {
    // For local development, it's always manual setup
    if (isLocalhostDomain(domain)) {
      return 'Manual';
    }
    return getStatusText(status);
  };

  /**
   * Copy instruction to clipboard
   */
  const copyToClipboard = async (text, index) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  /**
   * Render instruction
   */
  const renderInstruction = (instruction, index) => {
    const { type, title, command, description, platform } = instruction;
    const isCopied = copiedIndex === index;

    return (
      <div key={index} className={styles.instructionBlock}>
        <div className={styles.instructionHeader}>
          <div>
            <h4 className={styles.instructionTitle}>{title}</h4>
            {platform && <p className={styles.instructionPlatform}>Platform: {platform}</p>}
          </div>
          <span className={styles.instructionType}>{type}</span>
        </div>

        <div className={styles.instructionContent}>
          <code>{command}</code>
          {description && <p className={styles.instructionDescription}>{description}</p>}
        </div>

        <button
          className={styles.copyButton}
          onClick={() => copyToClipboard(command, index)}
          title={isCopied ? 'Copied!' : 'Copy to clipboard'}
        >
          {isCopied ? <span className="flex items-center gap-1"><Check size={14} /> Copied!</span> : <span className="flex items-center gap-1"><Copy size={14} /> Copy</span>}
        </button>
      </div>
    );
  };

  return (
    <Card variant="outlined" className={styles.card}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <h3 className={styles.title}>Domain Setup</h3>
          <div className={styles.domain}>
            {getUserFacingUrl(domain)}
          </div>
        </div>
        <div className={styles.badges}>
          <Badge variant={getStatusBadgeVariant(status)}>
            Subdomain: {getSubdomainStatusText(status)}
          </Badge>
          <Badge variant={sslStatus === 'ready' || isLocalhostDomain(domain) ? 'success' : getStatusBadgeVariant(sslStatus)}>
            SSL: {getSSLStatusText(sslStatus)}
          </Badge>
        </div>
      </div>

      {/* Content */}
      <div className={styles.content}>
        {/* Error Alert */}
        {error && (
          <Alert variant="error" className={styles.alert}>
            <div>{error}</div>
          </Alert>
        )}

        {/* Status Message */}
        {isReady && !isLocalhostDomain(domain) && (
          <Alert variant="success" className={styles.alert}>
            <Check size={16} className="inline mr-2" /> Domain is fully provisioned and ready to use!
          </Alert>
        )}

        {isLocalhostDomain(domain) && (
          <Alert variant="info" className={styles.alert}>
            <HelpCircle size={16} className="inline mr-2" /> Manual setup required for local development. Follow the instructions below to add the domain to your hosts file.
          </Alert>
        )}

        {isPending && !isLocalhostDomain(domain) && (
          <Alert variant="info" className={styles.alert}>
            <div>
              Domain is being provisioned. This usually takes a few minutes...
              {isLoading && ' (checking status...)'}
            </div>
          </Alert>
        )}

        {isFailed && (
          <Alert variant="error" className={styles.alert}>
            Domain provisioning failed. Please check the instructions below or contact support.
          </Alert>
        )}

        {/* Details Grid - Side by Side on large screens */}
        <div className={styles.detailsGrid}>
          {/* Instructions */}
          {instructions && instructions.length > 0 && (
            <div className={styles.instructionsSection}>
              <div
                className={styles.sectionHeader}
                onClick={toggleInstructions}
                role="button"
                tabIndex={0}
              >
                <h4>Setup Instructions</h4>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  {isLoading && <span className={styles.updating}>Updating...</span>}
                  <span className={styles.collapseIcon}>
                    {isInstructionsExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </span>
                </div>
              </div>

              {isInstructionsExpanded && (
                <div className={styles.instructions}>
                  {instructions.map((instruction, index) =>
                    renderInstruction(instruction, index)
                  )}
                </div>
              )}
            </div>
          )}

          {/* DNS Records */}
          {dnsRecords && dnsRecords.length > 0 && (
            <div className={styles.dnsSection}>
              <button
                className={styles.toggleButton}
                onClick={() =>
                  setExpandedSection(expandedSection === 'dns' ? null : 'dns')
                }
              >
                <span>{expandedSection === 'dns' ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</span>
                DNS Records ({dnsRecords.length})
              </button>
              {expandedSection === 'dns' && (
                <div className={styles.dnsRecords}>
                  {dnsRecords.map((record, index) => (
                    <div key={index} className={styles.dnsRecord}>
                      <div className={styles.dnsRow}>
                        <div className={styles.dnsField}>
                          <label>Type</label>
                          <code>{record.type}</code>
                        </div>
                        <div className={styles.dnsField}>
                          <label>Name</label>
                          <code>{record.name}</code>
                        </div>
                        <div className={styles.dnsField}>
                          <label>Value</label>
                          <code>{record.value}</code>
                        </div>
                      </div>
                      <button
                        className={styles.copyButton}
                        onClick={() =>
                          copyToClipboard(
                            `${record.type} ${record.name} ${record.value}`,
                            `dns-${index}`
                          )
                        }
                      >
                        {copiedIndex === `dns-${index}` ? <span className="flex items-center gap-1"><Check size={14} /> Copied!</span> : <span className="flex items-center gap-1"><Copy size={14} /> Copy Record</span>}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Retry Button */}
        {(error || isFailed) && onRetry && (
          <Button
            onClick={onRetry}
            className={styles.retryButton}
            variant="secondary"
          >
            🔄 Retry Status Check
          </Button>
        )}

        {/* Helper Links */}
        {showDetails && (
          <div className={styles.details}>
            <p>Need help with domain setup?</p>
            <a href="/docs/domain-setup" target="_blank" rel="noopener noreferrer">
              View Documentation ↗
            </a>
          </div>
        )}
      </div>
    </Card>
  );
}

export default DomainProvisioningCard;
